import { authService } from './auth';
import { config } from '../config';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface LeagueType {
  leagueTypeId: string;
  name: string;
  season: string;
  description: string;
  registrationOpenAt: number;
  registrationCloseAt: number;
  seasonStartAt: number;
  seasonEndAt: number;
  status: 'upcoming' | 'registration_open' | 'active' | 'completed';
  liveSessionKey?: number;
  liveEventId?: string;
}

export interface League {
  leagueId: string;
  name: string;
  leagueTypeId?: string;
  leagueTypeName?: string;
  currentParticipants?: number;
  maxParticipants?: number;
  memberCount?: number;
  userRank?: number;
  userPoints?: number;
  joinCode?: string;
  commissionerUserId?: string;
  commissionerName?: string;
  isPrivate?: boolean;
  status?: string;
  createdAt?: string;
}

export interface Standing {
  rank: number;
  userId: string;
  displayName: string;
  totalPoints: number;
  totalGapAccuracy: number;
  rankChange: number;
  lastEventPoints: number;
}

export interface CreateUserProfileRequest {
  displayName: string;
}

export interface CreateLeagueRequest {
  name: string;
  leagueTypeId: string;
  maxParticipants: number;
  scoringConfig?: {
    pointsPerCorrectPosition: number;
    penaltyPerPositionOff: number;
  };
}

export interface JoinLeagueRequest {
  joinCode: string;
}

export interface InviteInfoResponse {
  joinCode: string;
  inviteToken: string | null;
  inviteUrl: string | null;
}

export interface InviteLinkResponse {
  inviteToken: string;
  inviteUrl: string;
  leagueId: string;
  createdAt: string;
  createdBy: string;
}

export interface RevokeInviteLinkResponse {
  message: string;
  leagueId: string;
  revokedAt: string;
}

export interface ValidateInviteTokenResponse {
  valid: boolean;
  league?: League;
  error?: string;
}

export interface JoinByTokenResponse {
  message: string;
  league: League;
}

export interface F1Driver {
  leagueTypeId: string;
  driver_number: number;
  broadcast_name: string;
  first_name: string;
  full_name: string;
  headshot_url: string | null;
  last_name: string;
  meeting_key: number;
  name_acronym: string;
  session_key: number;
  team_colour: string | null;
  team_name: string;
  updatedAt: number;
}

export interface F1Event {
  eventId: string;
  leagueTypeId: string;
  eventName: string;
  eventDate: number;
  deadline: number;
  status: 'upcoming' | 'in_progress' | 'completed' | 'cancelled';
  // Legacy optional fields
  sessionKey?: number;
  round?: number;
  circuit?: string;
  country?: string;
  // Rich event metadata
  meetingKey?: number;
  circuitKey?: number;
  circuitShortName?: string;
  circuitType?: string;
  circuitImage?: string;
  countryCode?: string;
  countryKey?: number;
  countryName?: string;
  countryFlag?: string;
  location?: string;
  year?: number;
  meetingName?: string;
  meetingOfficialName?: string;
  gmtOffset?: string;
  raceWeekendStart?: string;
  raceWeekendEnd?: string;
  sessionName?: string;
  sessionType?: string;
  raceEnd?: string;
  resultData?: {
    positions: { position: number; driver_number: number }[];
    actualGap?: number;
  };
}

export interface UserPrediction {
  predictionId: string;
  predictionData: {
    predictions: { position: number; driver_number: number }[];
    gapPrediction: number;
  };
  submittedAt: number;
}

export interface UserScore {
  points: number;
  scoreBreakdown: {
    totalPoints: number;
    positionScores: { position: number; predicted: string; actual: string; points: number }[];
  };
  gapAccuracy: number;
  calculatedAt: number;
}

export interface LeagueEventPrediction {
  prediction: UserPrediction | null;
  score: UserScore | null;
}

export interface MemberResult {
  userId: string;
  displayName: string;
  totalPoints: number;
  prediction: {
    predictionData: {
      predictions: { position: number; driver_number: number }[];
      gapPrediction: number;
    };
    submittedAt: number;
  } | null;
  score: {
    points: number;
    scoreBreakdown: {
      totalPoints: number;
      positionScores: { position: number; predicted: string; actual: string; points: number }[];
    };
    gapAccuracy: number;
    calculatedAt: number;
  } | null;
}

export interface LeagueEventResults {
  event: F1Event;
  results: MemberResult[];
}

export interface MemberEventPrediction {
  userId: string;
  predictions: { position: number; driver_number: number }[];
  submittedAt: number;
}

export interface LeagueEventPredictions {
  predictions: MemberEventPrediction[];
}

export interface SubmitPredictionResponse {
  predictionId: string;
  leagueId: string;
  eventId: string;
  submittedAt: string;
}

export interface LeaguePredictionStatus {
  eventId: string;
  submittedUserIds: string[];
}

export interface LeagueMember {
  userId: string;
  displayName: string;
  email?: string;
  commissionerNote?: string;
  joinedAt: number;
  totalPoints: number;
  currentRank: number;
  isCommissioner: boolean;
}

export interface LeagueMessage {
  messageId: string;
  content: string;
  sentBy: string;
  sentAt: number;
  isEdited?: boolean;
  editedAt?: number;
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const idToken = authService.getIdToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(idToken && { Authorization: `Bearer ${idToken}` }),
      ...options.headers,
    };

    const response = await fetch(`${config.api.endpoint}${endpoint}`, {
      ...options,
      headers,
    });

    // On 401, attempt token refresh once then retry
    if (response.status === 401 && !isRetry) {
      const newTokens = await authService.refreshTokens();
      if (newTokens) {
        return this.request<T>(endpoint, options, true);
      }
      // Refresh failed - clear auth state so user is redirected to login
      authService.clearTokens();
      authService.clearUserInfo();
      throw new ApiError(401, 'Session expired. Please log in again.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: { message: 'Request failed' } }));
      
      // Extract error message from nested structure
      let errorMessage = `HTTP ${response.status}`;
      if (errorData.error) {
        if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        } else if (errorData.error.message) {
          errorMessage = errorData.error.message;
        }
      } else if (errorData.message) {
        // Handle API Gateway error messages
        errorMessage = errorData.message;
      }
      
      // Store the full error object for later processing
      const apiError = new ApiError(response.status, errorMessage);
      (apiError as any).errorData = errorData;
      throw apiError;
    }

    if (response.status === 204) return undefined as unknown as T;
    return response.json();
  }

  async createLeague(data: CreateLeagueRequest): Promise<League> {
    return this.request<League>('/leagues', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createUserProfile(data: CreateUserProfileRequest): Promise<{ userId: string; displayName: string }> {
    // Note: User profiles are now auto-created on first auth, but keeping this for explicit creation
    return this.request<{ userId: string; displayName: string }>('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async joinLeague(data: JoinLeagueRequest): Promise<League> {
    return this.request<League>('/leagues/join', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getUserLeagues(): Promise<{ leagues: League[] }> {
    return this.request<{ leagues: League[] }>('/users/me/leagues');
  }

  async getUserLeaguePredictions(leagueId: string): Promise<{ 
    predictions: Record<string, { predictionId: string; submittedAt: number; hasPrediction: boolean; defaultSource?: string }> 
  }> {
    return this.request(`/leagues/${leagueId}/predictions/me`);
  }

  async getLeagueStandings(leagueId: string): Promise<{
    leagueId: string;
    leagueTypeId?: string;
    commissionerUserId?: string;
    standings: Standing[];
    lastUpdated: string;
    liveRaceSessionKey?: number;
    liveEventId?: string;
  }> {
    return this.request(`/leagues/${leagueId}/standings`);
  }

  async deleteAccount(): Promise<{
    message: string;
    userId: string;
    details: {
      cognitoDeleted: boolean;
      profileDeleted: boolean;
      dataAnonymized: boolean;
      leaguesProcessed: number;
    };
  }> {
    return this.request('/users/me', {
      method: 'DELETE',
    });
  }

  async getInviteLink(leagueId: string): Promise<InviteInfoResponse> {
    return this.request<InviteInfoResponse>(`/leagues/${leagueId}/invites`);
  }

  async generateInviteLink(leagueId: string): Promise<InviteLinkResponse> {
    return this.request<InviteLinkResponse>(`/leagues/${leagueId}/invites`, {
      method: 'POST',
    });
  }

  async validateInviteToken(token: string): Promise<ValidateInviteTokenResponse> {
    return this.request<ValidateInviteTokenResponse>(`/leagues/invites/${token}/validate`);
  }

  async joinLeagueByToken(inviteToken: string): Promise<JoinByTokenResponse> {
    return this.request<JoinByTokenResponse>('/leagues/join-by-token', {
      method: 'POST',
      body: JSON.stringify({ inviteToken }),
    });
  }

  async revokeInviteLink(leagueId: string): Promise<RevokeInviteLinkResponse> {
    return this.request<RevokeInviteLinkResponse>(`/leagues/${leagueId}/invites`, {
      method: 'DELETE',
    });
  }

  async getLeagueTypes(): Promise<{ leagueTypes: LeagueType[] }> {
    return this.request<{ leagueTypes: LeagueType[] }>('/league-types');
  }

  async getLeagueTypeEvents(leagueTypeId: string): Promise<{ events: F1Event[] }> {
    return this.request<{ events: F1Event[] }>(`/league-types/${encodeURIComponent(leagueTypeId)}/events`);
  }

  async getUpcomingEvents(leagueTypeId: string = 'default'): Promise<{ events: F1Event[] }> {
    return this.request<{ events: F1Event[] }>(`/events?leagueTypeId=${encodeURIComponent(leagueTypeId)}&status=upcoming`);
  }

  async getUserProfile(): Promise<{ userId: string; displayName: string; groups?: string[] }> {
    return this.request<{ userId: string; displayName: string; groups?: string[] }>('/users/me');
  }

  async updateUserProfile(data: { displayName: string }): Promise<{ userId: string; displayName: string; groups?: string[] }> {
    return this.request<{ userId: string; displayName: string; groups?: string[] }>('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/users/me/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async getEvents(
    leagueTypeId: string,
    status?: 'upcoming' | 'in_progress' | 'completed' | 'cancelled'
  ): Promise<{ events: F1Event[] }> {
    const params = new URLSearchParams({ leagueTypeId });
    if (status) params.set('status', status);
    return this.request<{ events: F1Event[] }>(`/events?${params}`);
  }

  async getEvent(eventId: string): Promise<{ event: F1Event }> {
    return this.request<{ event: F1Event }>(`/events/${eventId}`);
  }

  async getLeagueEventPrediction(leagueId: string, eventId: string): Promise<LeagueEventPrediction> {
    return this.request<LeagueEventPrediction>(`/leagues/${leagueId}/events/${eventId}/prediction`);
  }

  async submitPrediction(
    leagueId: string,
    eventId: string,
    predictions: { position: number; driver_number: number }[],
    gapPredictionSeconds: number
  ): Promise<SubmitPredictionResponse> {
    return this.request<SubmitPredictionResponse>(`/leagues/${leagueId}/events/${eventId}/prediction`, {
      method: 'POST',
      body: JSON.stringify({
        predictions,
        gapPrediction: Math.round(gapPredictionSeconds * 1000),
      }),
    });
  }

  async getLeagueEventResults(leagueId: string, eventId: string): Promise<LeagueEventResults> {
    return this.request<LeagueEventResults>(`/leagues/${leagueId}/events/${eventId}/results`);
  }

  async getLeagueEventPredictions(leagueId: string, eventId: string): Promise<LeagueEventPredictions> {
    return this.request<LeagueEventPredictions>(`/leagues/${leagueId}/events/${eventId}/predictions`);
  }

  async getLeaguePredictionStatus(leagueId: string, eventId: string): Promise<LeaguePredictionStatus> {
    return this.request<LeaguePredictionStatus>(`/leagues/${leagueId}/events/${eventId}/prediction-status`);
  }

  async getLeagueMembers(leagueId: string): Promise<{ members: LeagueMember[] }> {
    return this.request<{ members: LeagueMember[] }>(`/leagues/${leagueId}/members`);
  }

  async removeMember(leagueId: string, userId: string): Promise<{ message: string; userId: string }> {
    return this.request<{ message: string; userId: string }>(`/leagues/${leagueId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  async updateMemberNote(leagueId: string, userId: string, note: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/leagues/${leagueId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ note }),
    });
  }

  async postLeagueMessage(leagueId: string, content: string): Promise<LeagueMessage> {
    return this.request<LeagueMessage>(`/leagues/${leagueId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async getLeagueMessages(leagueId: string): Promise<{ messages: LeagueMessage[] }> {
    return this.request<{ messages: LeagueMessage[] }>(`/leagues/${leagueId}/messages`);
  }

  async editLeagueMessage(leagueId: string, messageId: string, content: string): Promise<LeagueMessage> {
    return this.request<LeagueMessage>(`/leagues/${leagueId}/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    });
  }

  async deleteLeagueMessage(leagueId: string, messageId: string): Promise<void> {
    await this.request<void>(`/leagues/${leagueId}/messages/${messageId}`, {
      method: 'DELETE',
    });
  }




  async triggerF1RaceSync(leagueTypeId: string): Promise<{ triggered: boolean; leagueTypeId: string; year: number }> {
    return this.request<{ triggered: boolean; leagueTypeId: string; year: number }>('/admin/f1/races/sync', {
      method: 'POST',
      body: JSON.stringify({ leagueTypeId }),
    });
  }

  async triggerF1DriverSync(leagueTypeId: string, eventId?: string): Promise<{ triggered: boolean; leagueTypeId: string; eventId?: string }> {
    const url = eventId 
      ? `/league-types/${encodeURIComponent(leagueTypeId)}/drivers/sync?eventId=${encodeURIComponent(eventId)}`
      : `/league-types/${encodeURIComponent(leagueTypeId)}/drivers/sync`;
    return this.request<{ triggered: boolean; leagueTypeId: string; eventId?: string }>(url, {
      method: 'POST',
    });
  }

  async triggerF1LiveSync(eventId: string): Promise<{ triggered: boolean }> {
    return this.request<{ triggered: boolean }>(`/events/${encodeURIComponent(eventId)}/live/sync`, {
      method: 'POST',
    });
  }

  async triggerF1ResultsSync(eventId: string): Promise<{ triggered: boolean }> {
    return this.request<{ triggered: boolean }>(`/events/${encodeURIComponent(eventId)}/results/sync`, {
      method: 'POST',
    });
  }

  async triggerF1DefaultPredictions(eventId: string): Promise<{ triggered: boolean }> {
    return this.request<{ triggered: boolean }>(`/events/${encodeURIComponent(eventId)}/predictions/default`, {
      method: 'POST',
    });
  }

  async updateEventStatus(eventId: string, status: 'upcoming' | 'in_progress' | 'completed' | 'cancelled'): Promise<{ eventId: string; message: string }> {
    return this.request<{ eventId: string; message: string }>(`/events/${encodeURIComponent(eventId)}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async setManualLivePositions(
    eventId: string,
    positions: { driverNumber: number; position: number | string }[]
  ): Promise<{ success: boolean; sessionKey: number; changeCount: number; connectionCount: number; message: string }> {
    return this.request<{ success: boolean; sessionKey: number; changeCount: number; connectionCount: number; message: string }>(
      '/admin/live-positions',
      {
        method: 'POST',
        body: JSON.stringify({ eventId, positions }),
      }
    );
  }

  async setManualRaceResults(
    eventId: string,
    positions: { driverNumber: number; position: number }[],
    actualGap?: number
  ): Promise<{ success: boolean; eventId: string; sessionKey: number; positionsSynced: number; predictionsScored: number; message: string }> {
    return this.request<{ success: boolean; eventId: string; sessionKey: number; positionsSynced: number; predictionsScored: number; message: string }>(
      '/admin/race-results',
      {
        method: 'POST',
        body: JSON.stringify({ eventId, positions, actualGap }),
      }
    );
  }

  async getF1Drivers(leagueTypeId: string): Promise<{ drivers: F1Driver[] }> {
    return this.request<{ drivers: F1Driver[] }>(`/league-types/${encodeURIComponent(leagueTypeId)}/drivers`);
  }
}

export const apiService = new ApiService();
